import { useMemo, useState } from 'react'
import { Alert, Button, Card, Flex, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import {
  applyLocalList,
  byInstant,
  byText,
  localSortChange,
  sortColumn,
  useLocalListState,
} from '@/shared/lib/localList'
import { EmptyState } from '@/shared/ui/EmptyState'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { PageHeader } from '@/shared/ui/PageHeader'
import { PermissionNotice } from '@/shared/ui/PermissionNotice'
import { RowActions } from '@/shared/ui/table/RowActions'
import { PayComponentFormModal } from '@/features/hr/PayComponentFormModal'
import { PAY_COMPONENT_KINDS, fetchPayComponents, type PayComponent } from '@/features/hr/payApi'

/**
 * Ish haqi qismlari katalogi — «pulning yo'nalishi».
 *
 * <h2>⚠️ HUQUQSIZ SO'ROV UMUMAN YUBORILMAYDI</h2>
 * O'qish huquqi — `hr.salary.view` (`PayComponentController` javadoc'i),
 * ya'ni AYNAN maosh huquqi. Shuning uchun bu yerda ham `SalaryCard`
 * dagi naqsh qo'llanadi va uchta oyog'i ham majburiy:
 * <ul>
 *   <li>huquq yo'q bo'lsa `useQuery` ISHGA TUSHMAYDI (`enabled`);</li>
 *   <li>niqob (`***`) yo'q — niqoblangan qator «bu yerda nimadir bor»
 *       deb turadi;</li>
 *   <li>o'rniga SABAB va yetishmayotgan huquq KODI yoziladi
 *       (`PermissionNotice`).</li>
 * </ul>
 *
 * <h2>⚠️ MARSHRUTDA `RequirePermission` YO'Q — VA BU ATAYLAB</h2>
 * Qo'yilsa, quyidagi `PermissionNotice` shoxiga hech qachon
 * kirilmasdi, ya'ni u O'LIK KOD bo'lardi va uni tekshiradigan qorovul
 * YOLG'ON YASHIL berardi (`QARORLAR.md` §27.4 naqshi). Ikkinchidan,
 * umumiy «Ruxsat yo'q» sahifasi eng muhim savolga javob bermaydi:
 * QAYSI huquqni so'rash kerak. Bu yerda kod xom holda, nusxa
 * olinadigan qilib chiqadi.
 *
 * <h2>⛔ SAHIFALASH YO'Q — SERVER UNI QO'LLAB-QUVVATLAMAYDI</h2>
 * `PayComponentController#list` yagona parametr oladi — `activeOnly`.
 * `page`/`size`/`sort`/`q` YO'Q, ya'ni javob doim butun katalog.
 * Butun ro'yxatni olib mijozda kesish ATAYLAB qilinmadi: sahifalash
 * borday ko'rsatib, aslida uni yasash ko'zbo'yamachilik bo'lardi
 * (egasi qarori, 2026-08-20). Qidiruv va saralash esa to'liq massiv
 * ustida ishlaydi va shuning uchun yolg'on tartib bermaydi
 * (`shared/lib/localList.ts`).
 *
 * <h2>⚠️ O'CHIRISH TUGMASI YO'Q</h2>
 * Backendda `DELETE` umuman yo'q. O'rniga «Faol» o'chiriladi
 * (arxivlash): mavjud maosh satrlari va tasdiqlangan varaqalar
 * joyida qoladi, yangi satr esa bu qismga yozilmaydi.
 *
 * <h2>⚠️ SUKUT BO'YICHA FAQAT FAOL QISMLAR (egasi qarori 2026-08-20)</h2>
 * «Yopilganlar qoidasi»: arxivlangan qism ro'yxatda ko'rinmaydi va u
 * HOLAT FILTRI orqali olinadi. Filtrning bo'sh holati yo'q — u
 * `active` dan boshlanadi. «Faol» serverga (`activeOnly`), «nofaol» esa
 * TO'LIQ ro'yxat ustida hisoblanadi (serverda bunday so'rov yo'q).
 *
 * <h2>⚠️ ASOSIY MAOSH QATORI TAHRIRLANADI, LEKIN QISMAN</h2>
 * `hr.pay_component_system_guard()` unda faqat `name` ni o'zgartirishga
 * ruxsat beradi. Tugmani butunlay yashirish operatorni nomni tuzata
 * olmaydigan holatga solardi, ochiq qoldirish esa tushunarsiz `23514`
 * berardi — shuning uchun tugma bor, maydonlar esa oynada qulflangan.
 */
export function HrPayComponentsPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canView = can('hr.salary.view')
  const canWrite = can('hr.paycomponent.write')

  const [editing, setEditing] = useState<PayComponent | null>(null)
  const [open, setOpen] = useState(false)

  // Standart tartib — «oxirgi qo'shilgan birinchi». `createdAt`
  // `PayComponentView` da BOR, ya'ni tartib o'ylab topilmagan.
  const list = useLocalListState({ defaultSort: { field: 'createdAt', order: 'descend' } })
  // ⚠️ Filtr qo'yilmagan holat = `active`, `null` EMAS (sinf javadoc'i).
  const activeFilter = list.filter('active') ?? 'active'
  // ⚠️ «Faol» SERVERGA boradi (bekorga qator tashilmasin), «nofaol»
  //    esa serverda umuman yo'q va to'liq ro'yxat ustida hisoblanadi.
  const activeOnly = activeFilter === 'active'

  const components = useQuery({
    queryKey: queryKeys.hr.payComponents(activeOnly),
    queryFn: () => fetchPayComponents(activeOnly),
    enabled: canView,
  })

  const rows = useMemo(() => components.data ?? [], [components.data])

  const visible = useMemo(
    () =>
      applyLocalList(rows, {
        search: list.search,
        searchFields: (row) => [row.code, row.name],
        filters: {
          active: (row, value) => (value === 'inactive' ? !row.active : true),
          kind: (row, value) => row.kind === value,
          recurring: (row, value) => row.recurring === (value === 'yes'),
        },
        filterState: list.state.filters,
        comparators: {
          code: byText((row) => row.code),
          name: byText((row) => row.name),
          createdAt: byInstant((row) => row.createdAt),
        },
        sort: list.sort,
      }),
    [rows, list.search, list.state.filters, list.sort],
  )

  if (!canView) {
    return (
      <>
        <PageHeader
          title={t('nav.hrPayComponents')}
          description={t('pageIntro.hrPayComponents')}
        />
        <Card>
          <PermissionNotice
            permission="hr.salary.view"
            title={t('hr.payComponent.hiddenTitle')}
            reason={t('hr.payComponent.hiddenReason')}
          />
        </Card>
      </>
    )
  }

  const columns: ColumnsType<PayComponent> = [
    {
      // Sahifalash yo'q — katalog bir ekranda keladi
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_value, _row, index) => (
        <Typography.Text type="secondary">{index + 1}</Typography.Text>
      ),
    },
    {
      title: t('hr.payComponent.code'),
      key: 'code',
      ...sortColumn(list.sort, 'code'),
      render: (_value, row) => (
        <Space size={6} wrap>
          <span>{row.code}</span>
          {row.base ? <Tag color="blue">{t('hr.payComponent.baseTag')}</Tag> : null}
        </Space>
      ),
    },
    {
      title: t('hr.payComponent.name'),
      key: 'name',
      ...sortColumn(list.sort, 'name'),
      render: (_value, row) => seededName('payComponents', row.code, row.name, t),
    },
    {
      title: t('hr.payComponent.kind'),
      key: 'kind',
      render: (_value, row) => (
        <Tag color={row.kind === 'EARNING' ? 'green' : 'volcano'}>
          {t(`payComponentKind.${row.kind}`)}
        </Tag>
      ),
    },
    {
      title: t('hr.payComponent.recurring'),
      key: 'recurring',
      render: (_value, row) =>
        row.recurring ? (
          <Tag color="blue">{t('hr.payComponent.recurringYes')}</Tag>
        ) : (
          <Tag>{t('hr.payComponent.recurringNo')}</Tag>
        ),
    },
    {
      title: t('hr.payComponent.active'),
      key: 'active',
      render: (_value, row) =>
        row.active ? (
          <Tag color="green">{t('common.yes')}</Tag>
        ) : (
          <Tag color="default">{t('hr.payComponent.archived')}</Tag>
        ),
    },
    {
      title: t('common.createdAt'),
      key: 'createdAt',
      width: 180,
      ...sortColumn(list.sort, 'createdAt'),
      // ⚠️ Standart tartib aynan shu ustun bo'yicha — ko'rsatilmasa
      //    «nega bu qator tepada?» degan savol javobsiz qolardi.
      render: (_value, row) =>
        row.createdAt === null ? (
          <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
        ) : (
          <Tooltip title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</Tooltip>
        ),
    },
    ...(canWrite
      ? [
          {
            title: '',
            key: 'actions',
            width: 56,
            align: 'center' as const,
            fixed: 'right' as const,
            render: (_value: unknown, row: PayComponent) => (
              <RowActions
                items={[
                  {
                    key: 'edit',
                    label: t('common.edit'),
                    onClick: () => {
                      setEditing(row)
                      setOpen(true)
                    },
                    permission: 'hr.paycomponent.write',
                  },
                ]}
              />
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <PageHeader
        title={t('nav.hrPayComponents')}
        description={t('pageIntro.hrPayComponents')}
        extra={
          <Can permission="hr.paycomponent.write">
            <Button
              type="primary"
              onClick={() => {
                setEditing(null)
                setOpen(true)
              }}
            >
              {t('hr.payComponent.createAction')}
            </Button>
          </Can>
        }
      />

      <Card>
        {components.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(components.error, t)}
          />
        ) : null}

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <ExpandableHint text={t('hr.payComponent.listHint')} fontSize={13} />

          {/* Qidiruv va filtrlar KO'RINADIGAN joyda — ustun voronkasi
              ichidagi boshqaruvni operator topmaydi (etalon jadvalda
              o'lchangan). */}
          <Flex wrap gap="small" align="center">
            <Input.Search
              allowClear
              value={list.searchText}
              onChange={(e) => list.setSearchText(e.target.value)}
              placeholder={t('hr.payComponent.searchPlaceholder')}
              style={{ width: 260, maxWidth: '100%' }}
            />
            <Select<string>
              allowClear
              value={list.filter('kind')}
              onChange={(value) => list.setFilter('kind', value ?? null)}
              placeholder={t('hr.payComponent.kind')}
              style={{ width: 170 }}
              options={PAY_COMPONENT_KINDS.map((kind) => ({
                value: kind,
                label: t(`payComponentKind.${kind}`),
              }))}
            />
            <Select<string>
              allowClear
              value={list.filter('recurring')}
              onChange={(value) => list.setFilter('recurring', value ?? null)}
              placeholder={t('hr.payComponent.recurring')}
              style={{ width: 180 }}
              options={[
                { value: 'yes', label: t('hr.payComponent.recurringYes') },
                { value: 'no', label: t('hr.payComponent.recurringNo') },
              ]}
            />
            {/* ⚠️ `allowClear` YO'Q: bo'sh holat «hammasi» degan ma'no
                berardi, aslida esa sukut — faqat faollar. */}
            <Select<string>
              value={activeFilter}
              onChange={(value) => list.setFilter('active', value)}
              aria-label={t('listView.statusFilter')}
              style={{ width: 170 }}
              options={[
                { value: 'active', label: t('listView.active') },
                { value: 'inactive', label: t('listView.inactive') },
              ]}
            />
            {list.narrowed ? (
              <>
                <Typography.Text type="secondary">
                  {t('listView.shown', { shown: visible.length, total: rows.length })}
                </Typography.Text>
                <Button type="link" size="small" onClick={list.reset}>
                  {t('dataTable.clearFilters')}
                </Button>
              </>
            ) : null}
          </Flex>

          {/* ⚠️ Bo'sh xulosa FAQAT `isSuccess` da (`guards.mjs` 3-bo'limi).
              ⚠️ Filtr hech narsa topmagan holat BU YERGA TUSHMAYDI: u
                 jadval ichida ko'rsatiladi, chunki filtrlar jadval
                 tepasida turadi va ularsiz operator ro'yxatni qaytara
                 olmasdi. */}
          {components.isSuccess && rows.length === 0 ? (
            activeOnly ? (
              // ⚠️ Server FAOLLARNI so'radi va bittasini ham topmadi.
              //    «Katalog bo'sh» deyish yolg'on bo'lardi: arxivlangan
              //    qismlar turgan bo'lishi mumkin va ular bir bosishda
              //    ochiladi.
              <EmptyState
                title={t('listView.activeOnlyEmpty')}
                description={t('listView.activeOnlyEmptyHint')}
                actionLabel={t('listView.activeOnlyEmptyAction')}
                onAction={() => list.setFilter('active', 'inactive')}
              />
            ) : (
              <EmptyState
                title={t('hr.payComponent.empty')}
                description={t('hr.payComponent.emptyHint')}
                {...(canWrite
                  ? {
                      actionLabel: t('hr.payComponent.createAction'),
                      onAction: () => {
                        setEditing(null)
                        setOpen(true)
                      },
                    }
                  : {})}
              />
            )
          ) : (
            <Table<PayComponent>
              rowKey={(row) => row.id}
              columns={columns}
              dataSource={[...visible]}
              loading={components.isPending}
              pagination={false}
              size="small"
              scroll={{ x: 1080 }}
              onChange={localSortChange<PayComponent>(list.patch)}
              locale={{
                emptyText: (
                  <EmptyState
                    variant="no-data"
                    title={t('listView.noMatch')}
                    description={t('listView.noMatchHint', { total: rows.length })}
                    actionLabel={t('dataTable.clearFilters')}
                    onAction={list.reset}
                  />
                ),
              }}
            />
          )}

          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('listView.fullListNote')}
          </Typography.Text>
        </Space>
      </Card>

      <PayComponentFormModal
        open={open}
        component={editing}
        onClose={() => setOpen(false)}
      />
    </>
  )
}
